import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/** @jsx jsx */
import { forwardRef, useContext } from 'react';
import { css, jsx } from '@emotion/react';
import { easeOut, prefersReducedMotion } from '@atlaskit/motion';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { UNSAFE_media } from '@atlaskit/primitives/responsive';
import { COLLAPSED_LEFT_SIDEBAR_WIDTH, LEFT_SIDEBAR_FLYOUT_WIDTH, LEFT_SIDEBAR_WIDTH, MAX_MOBILE_SIDEBAR_FLYOUT_WIDTH, MOBILE_COLLAPSED_LEFT_SIDEBAR_WIDTH, TRANSITION_DURATION } from '../../../common/constants';
import { useIsSidebarDragging } from '../../../common/hooks';
import { getPageLayoutSlotSelector } from '../../../common/utils';
import { SidebarResizeContext } from '../../../controllers';
import SlotFocusRing from './slot-focus-ring';
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var prefersReducedMotionStyles = css(prefersReducedMotion());

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- With a feature flag, this does not apply
var mobileStyles = getBooleanFF('platform.design-system-team.responsive-page-layout-left-sidebar_p8r7g') ? css(_defineProperty({}, UNSAFE_media.below.sm, {
  width: MOBILE_COLLAPSED_LEFT_SIDEBAR_WIDTH,
  cursor: 'pointer',
  opacity: 1,
  transition: "width ".concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s"),
  '&::after': {
    display: 'inline-block',
    maxWidth: MAX_MOBILE_SIDEBAR_FLYOUT_WIDTH,
    content: "''"
  }
})) : undefined;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- With a feature flag, this does not apply
var mobileFlyoutStyles = getBooleanFF('platform.design-system-team.responsive-page-layout-left-sidebar_p8r7g') ? css(_defineProperty({}, UNSAFE_media.below.sm, {
  cursor: 'revert'
})) : undefined;
var outerStyles = css({
  width: LEFT_SIDEBAR_WIDTH,
  marginLeft: "var(--ds-space-0, 0px)",
  position: 'relative',
  zIndex: 1,
  transition: "width ".concat(TRANSITION_DURATION, "ms ").concat(easeOut, " 0s"),
  ':hover': {
    '--ds--resize-button--opacity': 1
  }
});
var draggingStyles = css({
  cursor: 'ew-resize',
  // Make sure drag to resize does not animate as the user drags
  transition: 'none'
});

/**
 * In fixed mode this element's child is taken out of the document flow.
 * It doesn't take up the width as expected,
 * so the pseudo element forces it to take up the necessary width.
 */
var fixedStyles = css({
  '::after': {
    display: 'inline-block',
    width: "".concat(LEFT_SIDEBAR_WIDTH),
    content: "''"
  }
});
var flyoutStyles = css({
  width: LEFT_SIDEBAR_FLYOUT_WIDTH
});
var flyoutFixedStyles = css({
  width: COLLAPSED_LEFT_SIDEBAR_WIDTH
});
var selector = getPageLayoutSlotSelector('left-sidebar');
var LeftSidebarOuter = function LeftSidebarOuter(_ref, ref) {
  var children = _ref.children,
    _ref$isFixed = _ref.isFixed,
    isFixed = _ref$isFixed === void 0 ? false : _ref$isFixed,
    testId = _ref.testId,
    onMouseLeave = _ref.onMouseLeave,
    onMouseOver = _ref.onMouseOver,
    onClick = _ref.onClick,
    id = _ref.id;
  var isDragging = useIsSidebarDragging();
  var _useContext = useContext(SidebarResizeContext),
    isFlyoutOpen = _useContext.leftSidebarState.isFlyoutOpen;
  return jsx(SlotFocusRing, {
    isSidebar: true
  }, function (_ref2) {
    var className = _ref2.className;
    return (
      /**
       * On desktop, the `onMouseOver|onMouseLeave` handlers controls the temporary flyout behavior.
       * This is an intentionally mouse-only experience, it may even be disruptive with keyboard navigation.
       *
       * On mobile, the `onClick` handler controls the toggled flyout behaviour.
       * This is not intended to be how you use this with a keyboard, there is a ResizeButton for this intentionally instead.
       */
      // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      jsx("div", _extends({
        css: [
        // mobile breakpoint styles
        mobileStyles, isFlyoutOpen && mobileFlyoutStyles,
        // generic styles
        outerStyles, isFixed && fixedStyles, isFlyoutOpen && flyoutStyles, isFlyoutOpen && isFixed && flyoutFixedStyles, isDragging && draggingStyles, prefersReducedMotionStyles],
        className: className,
        "data-testid": testId,
        id: id,
        onMouseOver: onMouseOver,
        onMouseLeave: onMouseLeave,
        onClick: onClick,
        ref: ref
      }, selector), children)
    );
  });
};
export default /*#__PURE__*/forwardRef(LeftSidebarOuter);